/**
 * AdminChannelsTab - Preset-driven tracking link generator + influencer performance.
 * Channels are auto-created via upsert when generating links (no manual channel management).
 */

import React, { useState, useEffect, useCallback } from 'react';
import { safeLocaleString } from '@/lib/utils';
import {
  getTrackingLinks,
  createTrackingLinkApi,
  deleteTrackingLinkApi,
  getDashboardInfluencers,
  type TrackingLink,
  type InfluencerRow,
} from '@/services/adminDashboardService';

// ============================================================================
// CHANNEL PRESETS — maps 1:1 to deriveChannel() rules in funnelService.ts
// ============================================================================

interface ChannelPreset {
  name: string;
  type: 'paid' | 'organic' | 'influencer' | 'email' | 'referral' | 'direct';
  utm_source: string;
  utm_medium: string;
  fixedCampaign?: string;
  showCampaign: boolean;
  showContent: boolean;
  showTerm: boolean;
  showInfluencer: boolean;
  contentLabel?: string;
  organicPresets?: string[];
}

const CHANNEL_PRESETS: ChannelPreset[] = [
  {
    name: 'Meta Ads',
    type: 'paid',
    utm_source: 'facebook',
    utm_medium: 'cpc',
    showCampaign: true,
    showContent: true,
    showTerm: true,
    showInfluencer: false,
    contentLabel: 'Criativo',
  },
  {
    name: 'Google Ads',
    type: 'paid',
    utm_source: 'google',
    utm_medium: 'cpc',
    showCampaign: true,
    showContent: true,
    showTerm: true,
    showInfluencer: false,
    contentLabel: 'Criativo',
  },
  {
    name: 'TikTok Ads',
    type: 'paid',
    utm_source: 'tiktok',
    utm_medium: 'cpc',
    showCampaign: true,
    showContent: true,
    showTerm: false,
    showInfluencer: false,
    contentLabel: 'Criativo',
  },
  {
    name: 'Instagram Global',
    type: 'organic',
    utm_source: 'instagram',
    utm_medium: 'organic',
    showCampaign: true,
    showContent: true,
    showTerm: false,
    showInfluencer: false,
    contentLabel: 'Posicionamento',
    organicPresets: ['bio', 'reels', 'stories', 'feed'],
  },
  {
    name: 'Instagram Espanhol',
    type: 'organic',
    utm_source: 'instagram',
    utm_medium: 'organic',
    fixedCampaign: 'es',
    showCampaign: false,
    showContent: true,
    showTerm: false,
    showInfluencer: false,
    contentLabel: 'Posicionamento',
    organicPresets: ['bio', 'reels', 'stories', 'feed'],
  },
  {
    name: 'TikTok Orgânico',
    type: 'organic',
    utm_source: 'tiktok',
    utm_medium: 'organic',
    showCampaign: true,
    showContent: true,
    showTerm: false,
    showInfluencer: false,
    contentLabel: 'Posicionamento',
    organicPresets: ['bio', 'reels', 'stories', 'feed'],
  },
  {
    name: 'YouTube',
    type: 'organic',
    utm_source: 'youtube',
    utm_medium: 'organic',
    showCampaign: true,
    showContent: true,
    showTerm: false,
    showInfluencer: false,
    contentLabel: 'Posicionamento',
  },
  {
    name: 'E-mail',
    type: 'email',
    utm_source: 'email',
    utm_medium: 'email',
    showCampaign: true,
    showContent: false,
    showTerm: false,
    showInfluencer: false,
  },
  {
    name: 'Afiliados',
    type: 'referral',
    utm_source: 'affiliate',
    utm_medium: 'affiliate',
    showCampaign: true,
    showContent: false,
    showTerm: false,
    showInfluencer: false,
  },
  {
    name: 'Comunidade',
    type: 'organic',
    utm_source: 'whatsapp',
    utm_medium: 'social',
    showCampaign: false,
    showContent: true,
    showTerm: false,
    showInfluencer: false,
    contentLabel: 'Grupo/Canal',
  },
  {
    name: 'Influenciador',
    type: 'influencer',
    utm_source: 'influencer',
    utm_medium: 'referral',
    showCampaign: true,
    showContent: false,
    showTerm: false,
    showInfluencer: true,
  },
  {
    name: 'Referral',
    type: 'referral',
    utm_source: 'referral',
    utm_medium: 'referral',
    showCampaign: true,
    showContent: false,
    showTerm: false,
    showInfluencer: false,
  },
];

const INFLUENCER_PERIODS = [
  { value: '7d', label: '7 dias' },
  { value: '14d', label: '14 dias' },
  { value: '30d', label: '30 dias' },
  { value: '90d', label: '90 dias' },
];

// ============================================================================
// COMPONENT
// ============================================================================

const AdminChannelsTab: React.FC = () => {
  // Link generator state
  const [selectedPreset, setSelectedPreset] = useState<ChannelPreset | null>(null);
  const [linkLabel, setLinkLabel] = useState('');
  const [linkCampaign, setLinkCampaign] = useState('');
  const [linkContent, setLinkContent] = useState('');
  const [linkTerm, setLinkTerm] = useState('');
  const [linkInfluencer, setLinkInfluencer] = useState('');
  const [linkCoupon, setLinkCoupon] = useState('');
  const [generatedUrl, setGeneratedUrl] = useState('');
  const [creatingLink, setCreatingLink] = useState(false);
  const [copiedId, setCopiedId] = useState('');
  const [error, setError] = useState('');
  const [deletingId, setDeletingId] = useState('');

  // Link history
  const [links, setLinks] = useState<TrackingLink[]>([]);
  const [loadingLinks, setLoadingLinks] = useState(true);

  // Influencer performance
  const [influencers, setInfluencers] = useState<InfluencerRow[]>([]);
  const [infPeriod, setInfPeriod] = useState('30d');
  const [loadingInf, setLoadingInf] = useState(false);

  const fetchLinks = useCallback(async () => {
    setLoadingLinks(true);
    try {
      const data = await getTrackingLinks();
      setLinks(data.links);
    } catch (err) {
      console.error('Failed to fetch links', err);
    } finally {
      setLoadingLinks(false);
    }
  }, []);

  const fetchInfluencers = useCallback(async () => {
    setLoadingInf(true);
    try {
      const data = await getDashboardInfluencers(infPeriod);
      setInfluencers(data.data);
    } catch {
      // Silently fail — section just won't show
    } finally {
      setLoadingInf(false);
    }
  }, [infPeriod]);

  useEffect(() => {
    fetchLinks();
    fetchInfluencers();
  }, [fetchLinks, fetchInfluencers]);

  const handlePresetSelect = (presetName: string) => {
    const preset = CHANNEL_PRESETS.find((p) => p.name === presetName) || null;
    setSelectedPreset(preset);
    setGeneratedUrl('');
    setError('');
    setLinkCampaign(preset?.fixedCampaign || '');
    setLinkContent('');
    setLinkTerm('');
    setLinkInfluencer('');
    setLinkCoupon('');
  };

  const handleGenerateLink = async () => {
    if (!selectedPreset || !linkLabel.trim()) return;
    if (selectedPreset.showInfluencer && !linkInfluencer.trim()) return;

    setCreatingLink(true);
    setError('');
    try {
      const channelName = selectedPreset.showInfluencer && linkInfluencer
        ? `Influenciador: ${linkInfluencer.trim()}`
        : selectedPreset.name;

      const link = await createTrackingLinkApi({
        channel_name: channelName,
        channel_type: selectedPreset.type,
        label: linkLabel.trim(),
        utm_source: selectedPreset.utm_source,
        utm_medium: selectedPreset.utm_medium,
        utm_campaign: selectedPreset.fixedCampaign || linkCampaign || undefined,
        utm_content: linkContent || undefined,
        utm_term: linkTerm || undefined,
        influencer_id: linkInfluencer.trim() || undefined,
        coupon_code: linkCoupon || undefined,
      });
      if (link) {
        setGeneratedUrl(link.full_url);
        setLinks((prev) => [link, ...prev]);
      }
    } catch (err: any) {
      setError(`Erro ao gerar link: ${err?.message || String(err)}`);
    } finally {
      setCreatingLink(false);
    }
  };

  const handleCopy = async (url: string, id: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(id);
      setTimeout(() => setCopiedId(''), 2000);
    } catch {
      // Fallback
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Tem certeza que deseja excluir este link?')) return;
    setDeletingId(id);
    try {
      await deleteTrackingLinkApi(id);
      setLinks((prev) => prev.filter((l) => l.id !== id));
    } catch (err) {
      console.error('Failed to delete link', err);
    } finally {
      setDeletingId('');
    }
  };

  const canGenerate = selectedPreset && linkLabel.trim() && (!selectedPreset.showInfluencer || linkInfluencer.trim());

  // Influencer link lookup
  const getInfluencerLink = (influencerId: string): string | null => {
    const link = links.find((l) => l.influencer_id === influencerId);
    return link?.full_url || null;
  };

  const infTotals = influencers.reduce(
    (acc, inf) => ({
      accounts: acc.accounts + inf.accounts,
      payments: acc.payments + inf.payments,
      revenue: acc.revenue + inf.revenue,
    }),
    { accounts: 0, payments: 0, revenue: 0 },
  );

  if (loadingLinks) {
    return <div className="text-muted-foreground py-8 text-center">Carregando dados...</div>;
  }

  return (
    <div className="space-y-8">
      {/* ================================================================ */}
      {/* SECTION 1: Link Generator */}
      {/* ================================================================ */}
      <div className="border border-border rounded-lg p-6">
        <h3 className="text-sm font-semibold text-foreground mb-1">Gerador de Links de Rastreio</h3>
        <p className="text-xs text-muted-foreground mb-4">
          Selecione o tipo de canal e gere um link com UTMs pré-configurados para atribuição automática.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Preset selector */}
          <div>
            <label className="block text-xs text-muted-foreground mb-1">Canal *</label>
            <select
              value={selectedPreset?.name || ''}
              onChange={(e) => handlePresetSelect(e.target.value)}
              className="w-full px-3 py-1.5 text-sm rounded-md border border-border bg-background text-foreground"
            >
              <option value="">Selecionar canal...</option>
              {CHANNEL_PRESETS.map((p) => (
                <option key={p.name} value={p.name}>{p.name}</option>
              ))}
            </select>
          </div>

          {/* Label */}
          <div>
            <label className="block text-xs text-muted-foreground mb-1">Nome da campanha *</label>
            <input
              type="text"
              placeholder="Ex: Black Friday 2026"
              value={linkLabel}
              onChange={(e) => setLinkLabel(e.target.value)}
              className="w-full px-3 py-1.5 text-sm rounded-md border border-border bg-background text-foreground"
            />
          </div>

          {/* Influencer ID */}
          {selectedPreset?.showInfluencer && (
            <div>
              <label className="block text-xs text-muted-foreground mb-1">ID do Influenciador *</label>
              <input
                type="text"
                placeholder="nome-do-influenciador"
                value={linkInfluencer}
                onChange={(e) => setLinkInfluencer(e.target.value)}
                className="w-full px-3 py-1.5 text-sm rounded-md border border-border bg-background text-foreground"
              />
            </div>
          )}

          {/* Campaign */}
          {selectedPreset?.showCampaign && (
            <div>
              <label className="block text-xs text-muted-foreground mb-1">utm_campaign</label>
              <input
                type="text"
                placeholder="nome-da-campanha"
                value={linkCampaign}
                onChange={(e) => setLinkCampaign(e.target.value)}
                className="w-full px-3 py-1.5 text-sm rounded-md border border-border bg-background text-foreground"
              />
            </div>
          )}

          {/* Content */}
          {selectedPreset?.showContent && (
            <div>
              <label className="block text-xs text-muted-foreground mb-1">
                {selectedPreset.contentLabel || 'Criativo'} (utm_content)
              </label>
              <input
                type="text"
                placeholder={selectedPreset.organicPresets ? 'bio, reels, stories...' : 'criativo-a, banner-1...'}
                value={linkContent}
                onChange={(e) => setLinkContent(e.target.value)}
                className="w-full px-3 py-1.5 text-sm rounded-md border border-border bg-background text-foreground"
              />
              {selectedPreset.organicPresets && (
                <div className="flex gap-1 mt-1">
                  {selectedPreset.organicPresets.map((p) => (
                    <button
                      key={p}
                      onClick={() => setLinkContent(p)}
                      className={`px-2 py-0.5 text-xs rounded transition-colors ${
                        linkContent === p
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted text-muted-foreground hover:bg-muted/80'
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Term */}
          {selectedPreset?.showTerm && (
            <div>
              <label className="block text-xs text-muted-foreground mb-1">utm_term</label>
              <input
                type="text"
                placeholder="palavra-chave"
                value={linkTerm}
                onChange={(e) => setLinkTerm(e.target.value)}
                className="w-full px-3 py-1.5 text-sm rounded-md border border-border bg-background text-foreground"
              />
            </div>
          )}

          {/* Coupon */}
          <div>
            <label className="block text-xs text-muted-foreground mb-1">Cupom</label>
            <input
              type="text"
              placeholder="CUPOM10"
              value={linkCoupon}
              onChange={(e) => setLinkCoupon(e.target.value)}
              className="w-full px-3 py-1.5 text-sm rounded-md border border-border bg-background text-foreground"
            />
          </div>
        </div>

        {/* UTM preview (read-only) */}
        {selectedPreset && (
          <div className="mt-4 flex flex-wrap items-center gap-3 text-xs">
            <span className="text-muted-foreground">UTMs:</span>
            <span className="px-2 py-0.5 rounded bg-muted text-muted-foreground font-mono">
              source={selectedPreset.utm_source}
            </span>
            <span className="px-2 py-0.5 rounded bg-muted text-muted-foreground font-mono">
              medium={selectedPreset.utm_medium}
            </span>
            <span className="text-muted-foreground ml-2">→</span>
            <span className="text-foreground font-medium">
              Canal atribuído: {selectedPreset.showInfluencer && linkInfluencer
                ? `Influenciador: ${linkInfluencer}`
                : selectedPreset.name}
            </span>
          </div>
        )}

        {error && (
          <div className="mt-3 px-3 py-2 text-sm text-destructive bg-destructive/10 rounded-md">
            {error}
          </div>
        )}

        <div className="mt-4">
          <button
            onClick={handleGenerateLink}
            disabled={creatingLink || !canGenerate}
            className="px-4 py-2 text-sm rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors"
          >
            {creatingLink ? 'Gerando...' : 'Gerar Link'}
          </button>
        </div>

        {/* Generated URL */}
        {generatedUrl && (
          <div className="mt-4 p-3 bg-muted/50 rounded-lg">
            <div className="text-xs text-muted-foreground mb-1">Link gerado:</div>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-sm text-foreground break-all bg-background p-2 rounded border border-border">
                {generatedUrl}
              </code>
              <button
                onClick={() => handleCopy(generatedUrl, 'generated')}
                className="px-3 py-1.5 text-sm rounded-md bg-primary text-primary-foreground hover:bg-primary/90 shrink-0 transition-colors"
              >
                {copiedId === 'generated' ? 'Copiado!' : 'Copiar'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ================================================================ */}
      {/* SECTION 2: Link History */}
      {/* ================================================================ */}
      {links.length > 0 && (
        <div className="border border-border rounded-lg overflow-hidden">
          <h3 className="text-sm font-semibold text-foreground p-4 border-b border-border">
            Links Gerados ({links.length})
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted/50">
                  <th className="text-left px-4 py-2 font-medium text-muted-foreground">Nome</th>
                  <th className="text-left px-4 py-2 font-medium text-muted-foreground">Canal</th>
                  <th className="text-left px-4 py-2 font-medium text-muted-foreground">Campanha</th>
                  <th className="text-left px-4 py-2 font-medium text-muted-foreground">Criativo</th>
                  <th className="text-left px-4 py-2 font-medium text-muted-foreground">Influenciador</th>
                  <th className="text-right px-4 py-2 font-medium text-muted-foreground">Criado em</th>
                  <th className="text-center px-4 py-2 font-medium text-muted-foreground">Ação</th>
                </tr>
              </thead>
              <tbody>
                {links.map((link) => (
                  <tr key={link.id} className="border-t border-border hover:bg-muted/30">
                    <td className="px-4 py-2 font-medium text-foreground">{link.label}</td>
                    <td className="px-4 py-2 text-muted-foreground text-xs font-mono">
                      {link.utm_source}/{link.utm_medium}
                    </td>
                    <td className="px-4 py-2 text-muted-foreground">{link.utm_campaign || '—'}</td>
                    <td className="px-4 py-2 text-muted-foreground">{link.utm_content || '—'}</td>
                    <td className="px-4 py-2 text-muted-foreground">{link.influencer_id || '—'}</td>
                    <td className="text-right px-4 py-2 text-muted-foreground">
                      {new Date(link.created_at).toLocaleDateString('pt-BR')}
                    </td>
                    <td className="text-center px-4 py-2">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleCopy(link.full_url, link.id)}
                          className="px-2 py-1 text-xs rounded bg-muted text-muted-foreground hover:bg-muted/80 transition-colors"
                        >
                          {copiedId === link.id ? 'Copiado!' : 'Copiar'}
                        </button>
                        <button
                          onClick={() => handleDelete(link.id)}
                          disabled={deletingId === link.id}
                          className="p-1 text-muted-foreground hover:text-destructive transition-colors disabled:opacity-50"
                          title="Excluir link"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/>
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ================================================================ */}
      {/* SECTION 3: Influencer Performance */}
      {/* ================================================================ */}
      <div className="border border-border rounded-lg overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h3 className="text-sm font-semibold text-foreground">Performance de Influenciadores</h3>
          <div className="flex gap-2">
            {INFLUENCER_PERIODS.map((p) => (
              <button
                key={p.value}
                onClick={() => setInfPeriod(p.value)}
                className={`px-3 py-1 text-xs rounded-md transition-colors ${
                  infPeriod === p.value
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {loadingInf ? (
          <div className="text-muted-foreground py-6 text-center text-sm">Carregando...</div>
        ) : influencers.length === 0 ? (
          <div className="py-6 px-4 text-center">
            <p className="text-sm text-muted-foreground">
              Nenhum influenciador rastreado neste período.
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Selecione o preset "Influenciador" acima, preencha o ID e gere um link para começar a rastrear.
            </p>
          </div>
        ) : (
          <>
            {/* KPI summary */}
            <div className="grid grid-cols-3 gap-4 p-4 border-b border-border">
              <div>
                <div className="text-xs text-muted-foreground">Influenciadores</div>
                <div className="text-lg font-bold text-foreground">{influencers.length}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Conversão</div>
                <div className="text-lg font-bold text-foreground">
                  {infTotals.accounts > 0 ? ((infTotals.payments / infTotals.accounts) * 100).toFixed(1) : '0'}%
                </div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Receita Total</div>
                <div className="text-lg font-bold text-foreground">
                  R$ {safeLocaleString(infTotals.revenue)}
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/50">
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">Influenciador</th>
                    <th className="text-right px-4 py-3 font-medium text-muted-foreground">Contas</th>
                    <th className="text-right px-4 py-3 font-medium text-muted-foreground">Carrosséis</th>
                    <th className="text-right px-4 py-3 font-medium text-muted-foreground">Checkouts</th>
                    <th className="text-right px-4 py-3 font-medium text-muted-foreground">Pagamentos</th>
                    <th className="text-right px-4 py-3 font-medium text-muted-foreground">Conversão</th>
                    <th className="text-right px-4 py-3 font-medium text-muted-foreground">Receita</th>
                    <th className="text-right px-4 py-3 font-medium text-muted-foreground">Ticket Médio</th>
                    <th className="text-center px-4 py-3 font-medium text-muted-foreground">Link</th>
                  </tr>
                </thead>
                <tbody>
                  {influencers.map((inf) => {
                    const convRate = inf.accounts > 0 ? ((inf.payments / inf.accounts) * 100).toFixed(1) : '0';
                    const ticketMedio = inf.payments > 0 ? (inf.revenue / inf.payments).toFixed(2) : '0';
                    const link = getInfluencerLink(inf.influencer_id);

                    return (
                      <tr key={inf.influencer_id} className="border-t border-border hover:bg-muted/30">
                        <td className="px-4 py-3 font-medium text-foreground">{inf.influencer_id}</td>
                        <td className="text-right px-4 py-3 text-foreground">{inf.accounts}</td>
                        <td className="text-right px-4 py-3 text-foreground">{inf.carousels}</td>
                        <td className="text-right px-4 py-3 text-foreground">{inf.checkouts}</td>
                        <td className="text-right px-4 py-3 text-foreground">{inf.payments}</td>
                        <td className="text-right px-4 py-3 font-medium text-foreground">{convRate}%</td>
                        <td className="text-right px-4 py-3 text-foreground">
                          R$ {safeLocaleString(inf.revenue)}
                        </td>
                        <td className="text-right px-4 py-3 text-foreground">R$ {ticketMedio}</td>
                        <td className="text-center px-4 py-3">
                          {link ? (
                            <button
                              onClick={() => handleCopy(link, inf.influencer_id)}
                              className="px-2 py-1 text-xs rounded bg-muted text-muted-foreground hover:bg-muted/80 transition-colors"
                            >
                              {copiedId === inf.influencer_id ? 'Copiado!' : 'Copiar'}
                            </button>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminChannelsTab;
